import React from 'react';

import { Icon } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import ActionButton from 'components/action-button';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import { Box, Typography } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useOrderDetails } from 'hooks/use-order-details';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    maxWidth: 300,
    paddingBottom: spacing.$12,
    paddingTop: spacing.$8,
  },
  containerDesktop: {
    maxWidth: 500,
  },
  modalBody: {
    textAlign: 'center',
  },
});

const Heading = ModalHeading.withConfig({
  marginTop: '$12',
  marginRight: 0,
  marginBottom: '$4',
  marginLeft: 0,
});

const ErrorIcon = Icon.withConfig({
  variant: 'error',
  width: '$32',
  height: '$32',
});

interface IOrderCancelledModalProps {
  onDismiss: () => void;
  modalAppearanceEventMessage?: string;
}

const OrderCancelledModal: React.FC<React.PropsWithChildren<IOrderCancelledModalProps>> = ({
  onDismiss,
  modalAppearanceEventMessage = 'Order Cancelled',
}) => {
  const {
    serverOrder,
    reorder: { reordering },
  } = useOrderContext();
  const { order } = useOrderDetails(serverOrder?.rbiOrderId || null);
  const { handleReorderClick } = useReorder(order);
  const isDesktop = useIsDesktopBreakpoint();

  const modalHeading = t('cancelledOrderTitle');
  const modalBody = t('cancelledOrderAction');

  return (
    <Modal
      onDismiss={onDismiss}
      amplitudeEventData={{
        modalAppearanceEventMessage,
        modalMessage: modalBody,
      }}
    >
      <ModalContent alignItems="center">
        <Box style={[styles.container, isDesktop && styles.containerDesktop]}>
          <ErrorIcon {...hiddenAccessibilityPlatformProps} />
          <Heading>{modalHeading}</Heading>
          <Typography style={styles.modalBody}>{modalBody}</Typography>
          <ActionButton onPress={handleReorderClick} isLoading={reordering}>
            {t('reorder')}
          </ActionButton>
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default OrderCancelledModal;
